import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import {
    Dialog,
    DialogPanel,
    DialogTitle,
} from "lib/headlessui/components/dialog/dialog";
import {
    Transition,
    TransitionChild,
} from "lib/headlessui/components/transition/transition";
import React, { useEffect, useState } from "react";

interface IdleTimeoutModalProps {
    isOpen: boolean;
    timeRemainingMs: number;
    onStayLoggedIn: () => void;
    onLogout: () => void;
}

const formatTime = (ms: number): string => {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

export const IdleTimeoutModal: React.FC<IdleTimeoutModalProps> = ({
    isOpen,
    timeRemainingMs,
    onStayLoggedIn,
    onLogout,
}) => {
    const [displayTime, setDisplayTime] = useState(formatTime(timeRemainingMs));

    useEffect(() => {
        setDisplayTime(formatTime(timeRemainingMs));
    }, [timeRemainingMs]);

    useEffect(() => {
        if (!isOpen) return undefined;

        const interval = setInterval(() => {
            setDisplayTime(prev => {
                const [mins, secs] = prev.split(":").map(Number);
                const totalSeconds = mins * 60 + secs;
                if (totalSeconds <= 0) return "0:00";
                return formatTime((totalSeconds - 1) * 1000);
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [isOpen]);

    return (
        <Transition appear show={isOpen}>
            <Dialog as="div" className="relative z-1500" onClose={() => {}}>
                <TransitionChild
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0">
                    <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
                </TransitionChild>

                <div className="fixed inset-0 z-10 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <TransitionChild
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95">
                            <DialogPanel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
                                <div className="flex items-center space-x-4">
                                    <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-yellow-100">
                                        <ExclamationTriangleIcon
                                            className="h-6 w-6 text-yellow-600"
                                            aria-hidden="true"
                                        />
                                    </div>
                                    <div>
                                        <DialogTitle
                                            as="h3"
                                            className="text-lg font-semibold leading-6 text-gray-900">
                                            Session Timeout Warning
                                        </DialogTitle>
                                    </div>
                                </div>

                                <div className="mt-4">
                                    <p className="text-sm text-gray-600">
                                        Your session is about to expire due to inactivity.
                                    </p>
                                    <p className="mt-3 text-center">
                                        <span className="text-3xl font-bold text-gray-900">
                                            {displayTime}
                                        </span>
                                    </p>
                                    <p className="mt-1 text-center text-sm text-gray-500">
                                        until automatic logout
                                    </p>
                                </div>

                                <div className="mt-6 flex flex-col space-y-3 sm:flex-row sm:space-x-3 sm:space-y-0">
                                    <PrimaryButton
                                        onClick={onStayLoggedIn}
                                        className="flex-1 justify-center">
                                        Stay logged in
                                    </PrimaryButton>
                                    <SecondaryButton
                                        onClick={onLogout}
                                        className="flex-1 justify-center">
                                        Log out now
                                    </SecondaryButton>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};
